import { Injectable } from '@angular/core';

type ConsoleLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

/**
 * Keeps a rolling, in-memory buffer of the browser console so a bug report can ship the recent
 * logs + uncaught errors alongside the screenshot. Patches {@code console.*} once (still calling
 * the original methods) and listens for {@code error} / {@code unhandledrejection}.
 *
 * <p>Bounded: at most {@link MAX_ENTRIES} lines, each clipped — never grows unbounded over a long
 * session. Nothing leaves the browser until the user submits a report.
 */
@Injectable({ providedIn: 'root' })
export class ConsoleCaptureService {
  private static readonly MAX_ENTRIES = 300;
  private static readonly MAX_LINE = 1000;

  #buffer: string[] = [];
  #started = false;

  start(): void {
    if (this.#started || typeof window === 'undefined') return;
    this.#started = true;

    (['log', 'info', 'warn', 'error', 'debug'] as ConsoleLevel[]).forEach(level => {
      const original = console[level].bind(console);
      console[level] = (...args: unknown[]) => {
        this.#push(level, args);
        original(...args);
      };
    });

    window.addEventListener('error', e => this.#push('error', [e.message, `${e.filename}:${e.lineno}:${e.colno}`]));
    window.addEventListener('unhandledrejection', e => this.#push('error', ['UnhandledRejection', e.reason]));
  }

  /** Newline-joined snapshot of the buffer (oldest first), capped to {@code maxChars}. */
  snapshot(maxChars = 15000): string {
    const text = this.#buffer.join('\n');
    return text.length > maxChars ? text.slice(text.length - maxChars) : text;
  }

  #push(level: ConsoleLevel, args: unknown[]): void {
    const ts = new Date().toISOString().slice(11, 23);
    const msg = args.map(a => this.#format(a)).join(' ');
    this.#buffer.push(`[${ts}] ${level.toUpperCase()}: ${msg}`.slice(0, ConsoleCaptureService.MAX_LINE));
    if (this.#buffer.length > ConsoleCaptureService.MAX_ENTRIES) this.#buffer.shift();
  }

  #format(a: unknown): string {
    if (a instanceof Error) return a.stack || `${a.name}: ${a.message}`;
    if (typeof a === 'string') return a;
    if (typeof a === 'object' && a !== null) {
      try {
        return JSON.stringify(a);
      } catch {
        return String(a);
      }
    }
    return String(a);
  }
}
